import { EmojiSelectorButton } from 'editor/src/components/EditorHeader/components';
import { Tooltip, Flex, Tag, Typography, theme } from 'antd';
import {
  addDocumentReaction,
  fetchDocumentReactions,
  removeDocumentReaction,
} from '../../../features/editior/editorSlice';
import { EMOJI_MAP } from 'editor/src/components/EmojiPicker/data';
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SmilePlus from '../../../svg/SmilePlus';

const { Text } = Typography;
const { useToken } = theme;

const MAX_USER_TOOLTIP_THRESHOLD = 3;

export const AddEmojiButton = React.memo(({ open, onOpenChange, onClick, style = {}, ...rest }) => {
  return (
    <EmojiSelectorButton
      onClick={onClick}
      showRemove={false}
      open={open}
      onOpenChange={onOpenChange}
      placement="topLeft"
      {...rest}
    >
      <Flex align="center" style={{ height: '24px', cursor: 'pointer', ...style }}>
        <SmilePlus size={'21'} />
      </Flex>
    </EmojiSelectorButton>
  );
});

export const DocumentReactions = React.memo(({ user, fileId, workspaceId, topReactions }) => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const [globalReactionOpen, setGlobalReactionOpen] = useState(false);
  const { documentReactionError, reactions } = useSelector((state) => state.editor);

  useEffect(() => {
    dispatch(fetchDocumentReactions({ file_id: fileId, workspace_id: workspaceId }));
  }, [dispatch, fileId, workspaceId]);

  const onGlobalReactionOpenChange = useCallback((open) => setGlobalReactionOpen(open), [setGlobalReactionOpen]);

  const getEmojiForReaction = useCallback((reactionKey) => {
    return EMOJI_MAP[reactionKey] || '❓';
  }, []);

  const getUserCurrentReaction = useCallback(() => {
    for (const [reactionKey, reactionData] of Object.entries(reactions))
      if (reactionData?.users?.some((u) => u?.username === user?.username)) return reactionKey;
    return null;
  }, [reactions, user]);

  const handleReaction = useCallback(
    async (reactionKey) => {
      const currentReaction = getUserCurrentReaction();
      const file_id = fileId;
      const workspace_id = workspaceId;
      if (currentReaction === reactionKey) {
        const body = { action: 'DELETE' };
        await dispatch(removeDocumentReaction({ file_id, body, reaction: reactionKey, workspace_id }));
      } else {
        const body = { reaction: reactionKey };
        await dispatch(addDocumentReaction({ file_id, body, workspace_id }));
      }
      setGlobalReactionOpen(false);
    },
    [getUserCurrentReaction, dispatch, fileId, workspaceId]
  );

  const hasReactions = !documentReactionError && Object.values(reactions)?.length > 0;

  return (
    <Flex align={'center'} justify="flex-start" gap={token.paddingSM} className="document-reactions" wrap="wrap">
      <Flex gap={token.paddingXXS}>
        {!hasReactions &&
          topReactions.map((reaction) => {
            return (
              <Flex key={reaction} className="reaction-button" onClick={() => handleReaction(reaction)}>
                {getEmojiForReaction(reaction)}
              </Flex>
            );
          })}
      </Flex>
      {Object.entries(reactions).map(([reaction, reactionUsers]) => {
        const namesToShow = reactionUsers?.users?.slice(0, MAX_USER_TOOLTIP_THRESHOLD).map((u) => u.name);
        const remainingCount = reactionUsers?.users?.length - MAX_USER_TOOLTIP_THRESHOLD;

        return (
          <Tooltip
            styles={{ body: { fontSize: '12px' } }}
            title={(namesToShow || []).join(', ') + (remainingCount > 0 ? ` + ${remainingCount} others` : '')}
          >
            <Tag
              onClick={() => handleReaction(reaction)}
              color="processing"
              style={{ borderRadius: 999999, cursor: 'pointer', fontSize: '14px', padding: '1px 4px' }}
            >
              <span>{getEmojiForReaction(reaction)}</span>{' '}
              <span style={{ fontSize: token.fontSize }}>{reactionUsers?.count}</span>
            </Tag>
          </Tooltip>
        );
      })}
      <Flex align="center" gap={token.paddingXXS} justify="flex-start">
        <AddEmojiButton
          showPickerAlways={!hasReactions}
          style={{ paddingRight: token.paddingXS }}
          onClick={(value) => handleReaction(value?.code)}
          open={globalReactionOpen}
          onOpenChange={onGlobalReactionOpenChange}
          topReactions={topReactions}
        />
        {!hasReactions && <Text type="secondary">Be the first to add a reaction</Text>}
      </Flex>
    </Flex>
  );
});

export default DocumentReactions;
